import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import ScientificProjectListing from '../../components/ScientificPublications/ScientificProject/ScientificProjectListing';
import ScientificProjectForm from '../../components/ScientificPublications/ScientificProject/ScientificProjectForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { COUNTRIES_DATA } from '../../data/countriesData';
import { detectDuplicates } from '../../utils/duplicateChecker';
import DuplicateWarningModal from '../../components/DuplicateWarningModal';

const ScientificProject = () => {
    const today = new Date().toISOString().split('T')[0];
    const toast = useRef(null);

    const initialForm = {
        Title: '', ProjectCode: '', ProjectLevel: '', Summary: '',
        StartDate: today, EndDate: today, AcceptanceDate: '',
        Classification: '', Country: 'Việt Nam', MembersJSON: '[]', AttachmentUrl: ''
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [filters, setFilters] = useState({
        keyword: '', status: '', fromDate: null, toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'Title', direction: 'asc' });

    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [members, setMembers] = useState([]);
    const [editId, setEditId] = useState(null);

    const [users, setUsers] = useState([]);
    const [topicLevels, setTopicLevels] = useState([]);
    const [countries, setCountries] = useState([]);

    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUserId = useMemo(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                return Number(JSON.parse(userStr).Id);
            } catch (e) { return 0; }
        }
        return 0;
    }, []);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            const headers = getAuthHeaders();
            const [uRes, tlRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers }),
                fetch(`${baseUrl}/topiclevel`, { headers })
            ]);

            if (uRes.status === 401 || uRes.status === 403 || tlRes.status === 401 || tlRes.status === 403) {
                handleLogout();
                return;
            }

            if (uRes.ok) setUsers(await uRes.json());
            if (tlRes.ok) setTopicLevels(await tlRes.json());
        } catch (error) {
            console.error("Lỗi tải danh mục hệ thống:", error);
        }

        setCountries(COUNTRIES_DATA);

    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isSilent = false) => {
        if (!isSilent) setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            if (!currentUser || !currentUser.Id) {
                handleLogout();
                return;
            }

            const userRoleName = (currentUser.RoleName || '').toLowerCase();
            const isManager = currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true);

            const url = `${process.env.REACT_APP_API_BASE_URL}/scientificproject?userId=${currentUser.Id}&isAdmin=${isManager}`;

            const response = await fetch(url, { headers: getAuthHeaders() });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!', life: 3000 });
        } finally {
            if (!isSilent) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    useEffect(() => {
        let result = [...data];

        if (filters.keyword) {
            const kw = filters.keyword.toLowerCase();
            result = result.filter(item =>
                (item.Title?.toLowerCase().includes(kw)) ||
                (item.ProjectCode?.toLowerCase().includes(kw))
            );
        }

        if (filters.status) {
            result = result.filter(item => item.Status === filters.status);
        }

        if (filters.fromDate) {
            const filterFrom = new Date(filters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => {
                if (!item.StartDate) return false;
                return new Date(item.StartDate).getTime() >= filterFrom.getTime();
            });
        }

        if (filters.toDate) {
            const filterTo = new Date(filters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => {
                if (!item.StartDate) return false;
                return new Date(item.StartDate).getTime() <= filterTo.getTime();
            });
        }

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'AcceptanceDate') {
                    const dateA = a.AcceptanceDate ? new Date(a.AcceptanceDate).getTime() : 0;
                    const dateB = b.AcceptanceDate ? new Date(b.AcceptanceDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        setFilteredData(result);
    }, [data, filters, sortConfig]);

    useEffect(() => {
        if (data && data.length > 0) {
            const groups = detectDuplicates(data);
            setDuplicateGroups(groups);
        } else {
            setDuplicateGroups([]);
        }
    }, [data]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificproject?id=${id}&status=${encodeURIComponent(newStatus)}&userId=${userId}`, {
                method: 'PATCH',
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? "Gửi yêu cầu duyệt thành công!" : "Hủy yêu cầu duyệt thành công!",
                    life: 3000
                });
                fetchData(true);
            } else {
                const resData = await response.json();
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: resData.message || "Thao tác thất bại!", life: 3000 });
            }
        } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 }); }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Lưu nháp') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;

            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificproject?id=${id}&status=${encodeURIComponent(newStatus)}&userId=${userId}`, {
                    method: 'PATCH',
                    headers: getAuthHeaders()
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} đề tài!` : `Đã hủy gửi duyệt ${ids.length} đề tài!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = async (item) => {
        let parsedMembers = [];
        try {
            parsedMembers = JSON.parse(item.MembersJSON || '[]');
        } catch (e) {
            parsedMembers = [];
        }

        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.find(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: getAuthHeaders()
                });

                if (res.status === 401 || res.status === 403) {
                    handleLogout();
                    return;
                }

                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.find(u => u.Id === eu.Id)) {
                                merged.push(eu);
                            }
                        });
                        return merged;
                    });
                }
            } catch (error) {
                console.error("Lỗi tải thông tin thành viên bổ sung:", error);
            }
        }

        setEditId(item.Id);
        setFormData({ ...item });
        setMembers(parsedMembers);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa hồ sơ đã gửi hoặc đã duyệt!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa', message: 'Bạn có chắc chắn muốn xóa đề tài này?',
            accept: async () => {
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificproject?id=${id}&userId=${userId}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (res.status === 401 || res.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (res.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa bản ghi!' });
                        fetchData(true);
                    } else {
                        const err = await res.json();
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Xóa thất bại!' });
                    }
                } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' }); }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} đề tài đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;

                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificproject?id=${id}&userId=${userId}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setMembers([]); setEditId(null); };

    const exportToExcel = () => {
        const dataToExport = filteredData;

        if (dataToExport.length === 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const excelData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Mã đề tài': item.ProjectCode || '',
            'Tên đề tài': item.Title || '',
            'Cấp đề tài': item.ProjectLevel || '',
            'Phân loại': item.Classification || '',
            'Quốc gia': item.Country || '',
            'Ngày bắt đầu': item.StartDate ? new Date(item.StartDate).toLocaleDateString('vi-VN') : '',
            'Ngày kết thúc': item.EndDate ? new Date(item.EndDate).toLocaleDateString('vi-VN') : '',
            'Ngày nghiệm thu': item.AcceptanceDate ? new Date(item.AcceptanceDate).toLocaleDateString('vi-VN') : '',
            'Trạng thái': item.Status || 'Lưu nháp',
            'Người tạo': item.CreatorEmail || ''
        }));

        excelData.push({
            'STT': '',
            'Mã đề tài': 'TỔNG CỘNG:',
            'Tên đề tài': `${dataToExport.length} Đề tài`,
            'Cấp đề tài': '',
            'Phân loại': '',
            'Quốc gia': '',
            'Ngày bắt đầu': '',
            'Ngày kết thúc': '',
            'Ngày nghiệm thu': '',
            'Trạng thái': '',
            'Người tạo': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "DanhSachDeTai");

        const wscols = [
            { wch: 5 }, { wch: 15 }, { wch: 45 }, { wch: 20 }, { wch: 20 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 25 }
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `DanhSachDeTai_${today}.xlsx`);
    };

    const handleAddNew = () => {
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        setMembers([{
            userId: currentUser.Id || '',
            fullName: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(),
            role: 'Chủ nhiệm'
        }]);
        setEditId(null);
        setFormData(initialForm);
        setIsModalOpen(true);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={9999} style={{ zIndex: 9999 }} />
            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ ĐỀ TÀI</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={handleAddNew} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>

                {duplicateGroups.length > 0 && (
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#e74c3c', margin: 0, animation: 'pulse 2s infinite' }}
                        onClick={() => setIsDuplicateModalOpen(true)}
                    >
                        <i className="fa-solid fa-triangle-exclamation"></i> Phát hiện {duplicateGroups.length} nhóm trùng lặp
                    </button>
                )}
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH ĐỀ TÀI</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px'
                }}>
                    <div className="search-wrapper" style={{ position: 'relative', flex: isDesktop ? 'none' : '1 1 250px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input
                            type="text"
                            name="keyword"
                            placeholder="Tìm kiếm"
                            className="form-input"
                            style={{ paddingLeft: '35px', width: '100%' }}
                            value={filters.keyword}
                            onChange={handleFilterChange}
                        />
                    </div>

                    <select
                        name="status"
                        className="form-input"
                        style={{ flex: isDesktop ? 'none' : '1 1 200px' }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">Tất cả trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày (Bắt đầu)"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày (Bắt đầu)"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>
                </div>

                {(filters.keyword || filters.status || filters.fromDate || filters.toDate) && (
                    <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            className="btn-add-new"
                            style={{ backgroundColor: '#95a5a6', margin: 0 }}
                            onClick={() => setFilters({ keyword: '', status: '', fromDate: null, toDate: null })}
                        >
                            <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                        </button>
                    </div>
                )}
            </div>

            <ScientificProjectListing
                data={filteredData}
                searchQuery={filters.keyword}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleStatusChange(id, 'Lưu nháp')}
                onBulkSend={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkStatusChange(ids, 'Lưu nháp')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <ScientificProjectForm
                isOpen={isModalOpen}
                onClose={closeModal}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                users={users}
                countries={countries}
                topicLevels={topicLevels}
                members={members}
                setMembers={setMembers}
                toast={toast}
                fetchData={fetchData}
            />

            <DuplicateWarningModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                duplicateGroups={duplicateGroups}
                type="project"
                onDelete={handleDelete}
                currentUserId={currentUserId}
            />
        </div>
    );
};

export default ScientificProject;